<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" routerLink="/admin/dashboard">Admin</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page"
        >Feedback</span
      >
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">Feedback</h1>
        <p class="page-head__subtitle">
          Suggestions and bugs users submitted from the in-app Report button, with the page and
          device they were on. Internal only; never shown to clients.
        </p>
      </div>
    </div>
  </header>

  <!-- ─── Triage summary — counts across ALL reports, not this page ──────── -->
  @if (summary(); as s) {
    <section class="dash-section" aria-label="Report summary">
      <div class="dash-kpi-grid dash-kpi-grid--4">
        <div class="dash-kpi dash-kpi--compact">
          <div class="dash-kpi__top">
            <span class="dash-kpi__icon"><app-icon name="inbox" [size]="18" /></span>
          </div>
          <div>
            <p class="dash-kpi__value">{{ s.open | dash }}</p>
            <p class="dash-kpi__label">Still to resolve</p>
          </div>
        </div>

        <div class="dash-kpi dash-kpi--compact">
          <div class="dash-kpi__top">
            <span class="dash-kpi__icon"><app-icon name="alert-triangle" [size]="18" /></span>
          </div>
          <div>
            <p class="dash-kpi__value">{{ s.new | dash }}</p>
            <p class="dash-kpi__label">New</p>
          </div>
        </div>

        <div class="dash-kpi dash-kpi--compact">
          <div class="dash-kpi__top">
            <span class="dash-kpi__icon"><app-icon name="clock" [size]="18" /></span>
          </div>
          <div>
            <p class="dash-kpi__value">{{ s.in_progress | dash }}</p>
            <p class="dash-kpi__label">In progress</p>
          </div>
        </div>

        <div class="dash-kpi dash-kpi--compact">
          <div class="dash-kpi__top">
            <span class="dash-kpi__icon"><app-icon name="check-circle" [size]="18" /></span>
          </div>
          <div>
            <p class="dash-kpi__value">{{ s.resolved | dash }}</p>
            <p class="dash-kpi__label">Resolved</p>
          </div>
        </div>
      </div>
    </section>
  }

  <!-- ─── Filters ────────────────────────────────────────────────────────── -->
  <section class="fb-filters" aria-label="Filters">
    <mat-form-field appearance="outline" class="fb-filters__field">
      <mat-label>Status</mat-label>
      <mat-select [value]="statusFilter()" (valueChange)="onStatusChange($event)">
        <mat-option value="">All statuses</mat-option>
        @for (s of statusOptions(); track s.value) {
          <mat-option [value]="s.value">{{ s.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="fb-filters__field">
      <mat-label>Type</mat-label>
      <mat-select [value]="kindFilter()" (valueChange)="onKindChange($event)">
        <mat-option value="">All types</mat-option>
        @for (k of kindOptions(); track k.value) {
          <mat-option [value]="k.value">{{ k.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="fb-filters__field">
      <mat-label>Search</mat-label>
      <input
        matInput
        type="search"
        placeholder="Reference, message, page or reporter"
        [value]="search()"
        (change)="onSearch($any($event.target).value)"
      />
    </mat-form-field>

    <mat-slide-toggle
      class="fb-filters__toggle"
      [checked]="openOnly()"
      (change)="onOpenOnlyChange($event.checked)"
    >
      Only what's still to resolve
    </mat-slide-toggle>
  </section>

  <!-- ═════ TABLE ════════════════════════════════════════════════════════ -->
  <section class="dash-section" aria-label="Feedback">
    <div class="sg-panel dash-panel">
      <div class="dash-table-wrap">
        @if (loading()) {
          <table class="sg-table sg-table--skeleton">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Type</th>
                <th>What they reported</th>
                <th>Reported by</th>
                <th>Status</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              @for (r of [1, 2, 3, 4, 5, 6, 7, 8]; track r) {
                <tr>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--chip"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--text" style="width: 85%"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w70"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--chip"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span></td>
                </tr>
              }
            </tbody>
          </table>
        } @else if (error()) {
          <div class="sg-empty sg-empty--bordered">
            <app-icon name="x-circle" [size]="40" />
            <p class="sg-empty__title">Couldn't load reports</p>
            <p class="sg-empty__desc">Something went wrong on our side.</p>
            <button mat-stroked-button (click)="retry()">
              <app-icon name="refresh" [size]="18" /> Try again
            </button>
          </div>
        } @else if (!reports().length && isFiltered()) {
          <div class="sg-empty sg-empty--bordered">
            <app-icon name="search" [size]="40" />
            <p class="sg-empty__title">No results</p>
            <p class="sg-empty__desc">No reports match the filters you've set.</p>
            <button mat-stroked-button (click)="clearFilters()">
              <app-icon name="x" [size]="18" /> Clear filters
            </button>
          </div>
        } @else if (!reports().length) {
          <div class="dash-chart-empty dash-chart-empty--pad">
            <app-icon name="message-square" [size]="32" />
            <p>
              {{ openOnly() ? 'Nothing left to resolve' : 'No reports yet' }}
            </p>
          </div>
        } @else {
          <table class="sg-table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Type</th>
                <th>What they reported</th>
                <th>Reported by</th>
                <th>Status</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              @for (r of reports(); track r.id) {
                <tr class="sg-table__row">
                  <td>
                    <a class="fb-ref" [routerLink]="['/admin/feedback', r.short_id]">
                      {{ r.short_id }}
                    </a>
                  </td>
                  <td>
                    <span class="fb-kind">
                      <app-icon [name]="r.kind === 'BUG' ? 'bug' : r.kind === 'TOOL_REQUEST' ? 'zap' : 'lightbulb'" [size]="14" />
                      {{ r.kind_display }}
                    </span>
                  </td>
                  <td class="fb-message">
                    <span class="fb-message__text">{{ r.message }}</span>
                  </td>
                  <td class="fb-muted">{{ r.reporter_name | dash }}</td>
                  <td><app-status-chip [status]="r.triage_status" /></td>
                  <td class="fb-muted">{{ r.created_at | date: 'medium' }}</td>
                </tr>
              }
            </tbody>
          </table>
        }
      </div>

      @if (!loading() && !error() && showPager()) {
        <div class="fb-pager">
          <span class="fb-pager__range">
            {{ rangeStart() }}–{{ rangeEnd() }} of {{ total() | number }}
          </span>
          <div class="fb-pager__nav">
            <button mat-stroked-button type="button" (click)="prevPage()" [disabled]="page() <= 1">
              <app-icon name="chevron-left" [size]="16" /> Previous
            </button>
            <button mat-stroked-button type="button" (click)="nextPage()" [disabled]="!hasNext()">
              Next <app-icon name="chevron-right" [size]="16" />
            </button>
          </div>
        </div>
      }
    </div>
  </section>
</div>
